import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
} from '@mui/material';
import { apiService } from '../../services/apiService';
import { getVendorLabel, describeApiError } from './agentMeta';

const TIME_RANGES = [
  { value: 24, label: 'Last 24 hours' },
  { value: 72, label: 'Last 3 days' },
  { value: 168, label: 'Last 7 days' },
];

const StatCard = ({ label, value, hint }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h4" sx={{ fontWeight: 600 }}>{value}</Typography>
      {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
    </CardContent>
  </Card>
);

/** Request statistics for one agent over a selectable window. */
const AnalyticsTab = ({ agents = [], types = [] }) => {
  const [agentId, setAgentId] = useState('');
  const [timeRange, setTimeRange] = useState(24);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!agentId && agents.length) setAgentId(agents[0].id || agents[0]._id);
  }, [agents, agentId]);

  const load = useCallback(async () => {
    if (!agentId) return;
    try {
      setLoading(true);
      setError(null);
      const res = await apiService.agents.getStats(agentId, { timeRange });
      setData(res.data);
    } catch (err) {
      setError(describeApiError(err, 'Failed to load statistics').message);
    } finally {
      setLoading(false);
    }
  }, [agentId, timeRange]);

  useEffect(() => { load(); }, [load]);

  const stats = (data && data.stats) || {};
  const windowRate = stats.totalRequests ? Math.round((stats.successfulRequests / stats.totalRequests) * 100) : null;
  const agentInfo = data && data.agent;

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 3, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel>Agent</InputLabel>
          <Select value={agentId} label="Agent" onChange={(e) => setAgentId(e.target.value)}>
            {agents.map((a) => <MenuItem key={a.id || a._id} value={a.id || a._id}>{a.name}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Time range</InputLabel>
          <Select value={timeRange} label="Time range" onChange={(e) => setTimeRange(Number(e.target.value))}>
            {TIME_RANGES.map((r) => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
          </Select>
        </FormControl>
        {loading && <CircularProgress size={20} />}
        {agentInfo && (
          <Box sx={{ ml: 'auto', display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip size="small" variant="outlined" label={getVendorLabel(agentInfo.type, types)} />
            <Chip size="small" label={agentInfo.isEnabled ? 'Enabled' : 'Disabled'} color={agentInfo.isEnabled ? 'success' : 'default'} />
            <Chip size="small" label={agentInfo.isConnected ? 'Connected' : 'Disconnected'} color={agentInfo.isConnected ? 'success' : 'error'} variant="outlined" />
          </Box>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {agents.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No agents configured.</Typography>
      ) : (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard label="Total requests" value={stats.totalRequests ?? '—'} hint={`in the selected window`} />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard label="Successful requests" value={stats.successfulRequests ?? '—'} />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard label="Avg response time" value={stats.averageResponseTime !== undefined ? `${Math.round(stats.averageResponseTime)} ms` : '—'} />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard label="Rate limited" value={stats.rateLimitedRequests ?? '—'} hint="requests throttled by the agent" />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <StatCard
                label="Success rate"
                value={windowRate === null ? (agentInfo ? `${agentInfo.successRate}%` : '—') : `${windowRate}%`}
                hint={windowRate === null ? 'lifetime (no requests in window)' : `lifetime ${agentInfo ? agentInfo.successRate : '—'}%`}
              />
            </Grid>
          </Grid>
          {windowRate !== null && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="caption" color="text.secondary">Success rate in window</Typography>
              <LinearProgress
                variant="determinate"
                value={windowRate}
                color={windowRate >= 90 ? 'success' : windowRate >= 60 ? 'warning' : 'error'}
                sx={{ height: 8, borderRadius: 4, mt: 0.5 }}
              />
            </Box>
          )}
          {stats.totalDataTransferred !== undefined && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              Data transferred: {(stats.totalDataTransferred / 1024).toFixed(1)} KB
            </Typography>
          )}
        </>
      )}
    </Box>
  );
};

export default AnalyticsTab;
